import type { SeasonBreakdown } from '@sea-snag/shared';
import { Card, CardContent } from '@sea-snag/ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface SeasonBreakdownPanelProps {
  breakdown: SeasonBreakdown;
  /** The season being measured against, for the column heading. */
  seasonLabel: string;
}

/**
 * The year split by the season.
 *
 * A fleet with a short configured season still trades outside it, and the
 * occupancy percentage — which counts season days only — cannot show that. This
 * panel does, and the month strip turns it into a decision: 75 charters starting
 * in September means September belongs in the season.
 *
 * It renders nothing when the whole year falls inside the season. There is no
 * information in three identical columns, and most fleets will see none.
 */
export function SeasonBreakdownPanel({ breakdown, seasonLabel }: SeasonBreakdownPanelProps) {
  if (breakdown.outOfSeason.bookings === 0 && breakdown.outOfSeason.bookedWeeks <= 0) return null;

  const money = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: breakdown.currency ?? 'EUR',
    maximumFractionDigits: 0,
  });

  const rows = [
    {
      label: 'Bookings',
      values: [
        String(breakdown.inSeason.bookings),
        String(breakdown.outOfSeason.bookings),
        String(breakdown.fullYear.bookings),
      ],
    },
    {
      label: 'Booked weeks',
      values: [
        String(breakdown.inSeason.bookedWeeks),
        String(breakdown.outOfSeason.bookedWeeks),
        String(breakdown.fullYear.bookedWeeks),
      ],
    },
    {
      label: 'Income',
      values: [
        money.format(breakdown.inSeason.income),
        money.format(breakdown.outOfSeason.income),
        money.format(breakdown.fullYear.income),
      ],
    },
  ];

  // Bars are scaled against the busiest month rather than the year, so a quiet
  // month next to a busy one is still visible.
  const peak = Math.max(...breakdown.outsideByMonth.map((m) => m.bookings), 1);

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">
        <div className="space-y-1">
          <h3 className="text-sm font-medium">Inside and outside the season</h3>
          <p className="text-muted-foreground text-xs">
            Season is {seasonLabel}. Bookings and income count on the side the charter starts;
            booked weeks are counted by day, so the grid below still adds up.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="py-2 pr-4 font-medium" />
                <th className="py-2 pr-4 text-right font-medium">In season</th>
                <th className="py-2 pr-4 text-right font-medium">Out of season</th>
                <th className="py-2 text-right font-medium">Full year</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-border/50 border-t">
                  <td className="text-muted-foreground py-2 pr-4">{row.label}</td>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={`py-2 text-right tabular-nums ${
                        i === 2 ? 'font-medium' : i === 1 ? 'text-muted-foreground' : ''
                      } ${i < 2 ? 'pr-4' : ''}`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {breakdown.outsideByMonth.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
              Outside the season, by charter month
            </h4>
            <ul className="space-y-1">
              {breakdown.outsideByMonth.map((m) => (
                <li key={m.month} className="flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground w-8 shrink-0">{MONTHS[m.month - 1]}</span>
                  <span className="bg-muted h-2 min-w-[2px] flex-1 overflow-hidden rounded-full">
                    <span
                      className="bg-primary/70 block h-full rounded-full"
                      style={{ width: `${(m.bookings / peak) * 100}%` }}
                    />
                  </span>
                  <span className="w-20 shrink-0 text-right tabular-nums">
                    {m.bookings} booking{m.bookings === 1 ? '' : 's'}
                  </span>
                  <span className="text-muted-foreground w-24 shrink-0 text-right tabular-nums">
                    {money.format(m.income)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {breakdown.straddling > 0 && (
          <p className="text-muted-foreground text-xs">
            {breakdown.straddling} charter{breakdown.straddling === 1 ? '' : 's'} cross the season
            boundary. Each counts on the side it starts, so its booked weeks and its booking count
            can sit on different sides.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
